import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import './styles.scss';

const Tooltip = ({ children, text, position = 'top', hideTriangle = false }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipStyle, setTooltipStyle] = useState({});
    const [triangleStyle, setTriangleStyle] = useState({});
    const containerRef = useRef(null);
    const tooltipRef = useRef(null);

    // Memoized positioning calculation to prevent unnecessary recalculations
    const calculatePosition = useCallback(() => {
        if (!containerRef.current || !tooltipRef.current) return;

        const container = containerRef.current.getBoundingClientRect();
        const tooltip = tooltipRef.current.getBoundingClientRect();
        const viewport = {
            width: window.innerWidth,
            height: window.innerHeight
        };

        let left, top;
        
        // Simple positioning based on preferred position
        if (position === 'top') {
            top = container.top - tooltip.height - 8;
            left = container.left + (container.width / 2) - (tooltip.width / 2);
        } else if (position === 'bottom') {
            top = container.bottom + 8;
            left = container.left + (container.width / 2) - (tooltip.width / 2);
        } else if (position === 'left') {
            top = container.top + (container.height / 2) - (tooltip.height / 2);
            left = container.left - tooltip.width - 8;
        } else if (position === 'right') {
            top = container.top + (container.height / 2) - (tooltip.height / 2);
            left = container.right + 8;
        }

        // Simple boundary checking - just ensure it's visible
        if (left < 10) left = 10;
        if (left + tooltip.width > viewport.width - 10) left = viewport.width - tooltip.width - 10;
        if (top < 10) top = 10;
        if (top + tooltip.height > viewport.height - 10) top = viewport.height - tooltip.height - 10;

        // Calculate triangle position to always point to the children element
        const triangleLeft = container.left + (container.width / 2) - left;

        setTooltipStyle({
            position: 'fixed',
            left: `${left}px`,
            top: `${top}px`,
            zIndex: 10000
        });

        setTriangleStyle({
            left: `${triangleLeft}px`,
            top: position === 'top' ? '100%' : 
                 position === 'bottom' ? '0' : 
                 position === 'left' ? '50%' : '50%',
            transform: position === 'left' || position === 'right' ? 'translateY(-50%)' : 'none'
        });
    }, [position]);

    // Memoized event handlers
    const handleMouseEnter = useCallback(() => setIsVisible(true), []);
    const handleMouseLeave = useCallback(() => setIsVisible(false), []);

    // Position calculation effect
    useEffect(() => {
        if (isVisible) {
            // Small delay to ensure tooltip is rendered before calculating position
            const timer = setTimeout(calculatePosition, 0);
            return () => clearTimeout(timer);
        }
    }, [isVisible, calculatePosition]);

    // Resize handler effect
    useEffect(() => {
        if (!isVisible) return;

        const handleResize = () => calculatePosition();
        window.addEventListener('resize', handleResize);
        
        return () => window.removeEventListener('resize', handleResize);
    }, [isVisible, calculatePosition]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            // Cleanup any remaining tooltips when component unmounts
            setIsVisible(false);
        };
    }, []);

    const tooltipElement = isVisible && (
        <div 
            ref={tooltipRef}
            className={`tooltip tooltip-${position}`}
            style={tooltipStyle}
        >
            {text}
            {!hideTriangle && (
                <div 
                    className="tooltip-triangle"
                    style={triangleStyle}
                />
            )}
        </div>
    );

    return (
        <>
            <div 
                ref={containerRef}
                className="tooltip-container"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {children}
            </div>
            {createPortal(tooltipElement, document.body)}
        </>
    );
};

export default Tooltip; 